/**
 * Shared timeout + retry policy for API reads.
 *
 * The function app runs on Consumption and scales to zero when idle, which on
 * our traffic it usually is. The first request after an idle spell routinely
 * stalls for tens of seconds while the instance loads, and the one after it
 * lands in ~100ms — so failing fast and retrying turns a blank screen into a
 * slow load. This started life inside userDataService for GET /user; it lives
 * here because every read hits the same cold app, not just that one.
 */

// Default for calls that are not retried (writes). Reads use the escalating
// per-attempt timeouts below instead.
export const REQUEST_TIMEOUT_MS = 15000;

// Per-attempt timeouts for retried reads.
//
// A warm instance answers in well under a second, so a read still running after
// a few seconds is almost certainly waiting on an instance that is still
// starting. Giving up quickly and retrying often lands on a warm instance, or
// on that same one once it has finished loading in the background.
//
// Retrying does not restart that loading, so what matters is total elapsed
// time, not the individual timeouts. These add up to ~86s, which clears the
// ~52s a fully cold instance has been measured to take. Later attempts rarely
// use their full budget — once the instance is up it answers immediately.
const ATTEMPT_TIMEOUTS_MS = [3000, 5000, 10000, 20000, 45000];

export const isTimeoutError = (error) => error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT'
  || /timeout/i.test(error?.message || '');

// Only retry what a retry can fix — timeouts, dropped connections and 5xx. A
// 4xx will fail identically every time, and retrying a 401 just delays logout.
export const isRetryableError = (error) =>
  isTimeoutError(error) || !error?.response || error.response.status >= 500;

const RETRY_BASE_DELAY_MS = 300;

/**
 * Wraps a function that performs a raw request. Must sit inside any layer that
 * flattens axios errors into plain Errors, or `error.response` is already gone
 * by the time we need it to decide whether retrying is worthwhile.
 *
 * Only wrap reads. A retried write can apply twice where the first attempt
 * actually landed and only the response was lost.
 *
 * `fn` receives the caller's own arguments plus a trailing `{ timeout }` for
 * this attempt. Existing call sites that ignore it simply keep their own
 * timeout; passing it through is what makes the escalation take effect.
 */
export const withRetry = (fn, { timeouts = ATTEMPT_TIMEOUTS_MS, label = 'request' } = {}) => async (...args) => {
  let lastError;
  const attempts = timeouts.length;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    const timeout = timeouts[attempt - 1];
    try {
      return await fn(...args, { timeout });
    } catch (error) {
      lastError = error;
      if (attempt === attempts || !isRetryableError(error)) break;
      const delay = RETRY_BASE_DELAY_MS * attempt;
      console.warn(`[${label}] attempt ${attempt}/${attempts} failed after ${timeout}ms (${error.message}) — retrying in ${delay}ms`);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  throw lastError;
};
